import React, { useState, useEffect, useCallback } from 'react';
import { useSidebar } from '../../contexts/SidebarContext';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { supabase } from '../../lib/supabase';
import advancedBillingService from '../../services/advancedBillingService';
import FinancialSummaryCard from './components/FinancialSummaryCard';
import InvoiceFilters from './components/InvoiceFilters';
import InvoiceTable from './components/InvoiceTable';
import PaymentUploadModal from './components/PaymentUploadModal';
import InvoiceDetailsModal from './components/InvoiceDetailsModal';
import MilestoneStatusCard from './components/MilestoneStatusCard';
import PhaseProgressionPanel from './components/PhaseProgressionPanel';
import CollectionTeamSection from './components/CollectionTeamSection';
import EditInvoiceModal from './components/EditInvoiceModal';
import { FileEdit } from 'lucide-react';


// ---------------------------------------------------------------------------
// Helper: format a number as currency using a given ISO currency code
// Falls back to EUR if the code is missing or invalid.
// ---------------------------------------------------------------------------
const formatCurrency = (value, currencyCode = 'EUR', locale = 'fr-FR') => {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode || 'EUR',
    }).format(value ?? 0);
  } catch {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
    }).format(value ?? 0);
  }
};

// ---------------------------------------------------------------------------
// Fetch all invoices with their country's currency_code via a single join
// ---------------------------------------------------------------------------
const fetchInvoicesFromSupabase = async () => {
  const { data, error } = await supabase
    .from('invoices')
    .select(`
      id,
      invoice_number,
      client_name,
      client_email,
      status,
      issue_date,
      due_date,
      payment_date,
      subtotal,
      tax_percentage,
      tax_amount,
      total_amount,
      amount_paid,
      balance_due,
      payment_method,
      notes,
      country_id,
      package_id,
      batch_id,
      client_id,
      supplier_id,
      created_at,
      updated_at,
      invoice_type,
      countries (
        currency_code,
        country_name,
        country_code
      )
    `)
    .order('created_at', { ascending: false });

  if (error) throw error;

  // Normalise shape used throughout the page
  return (data || []).map((inv) => ({
    ...inv,
    invoiceNumber: inv.invoice_number,
    clientName: inv.client_name,
    clientEmail: inv.client_email,
    issueDate: inv.issue_date,
    dueDate: inv.due_date,
    paymentDate: inv.payment_date,
    totalAmount: inv.total_amount,
    amountPaid: inv.amount_paid,
    balanceDue: inv.balance_due,
    paymentMethod: inv.payment_method,
    // Currency resolved from the joined country row
    currencyCode: inv.countries?.currency_code || 'EUR',
    countryName: inv.countries?.country_name || '',
    invoiceType: inv.invoice_type === 'design_fee' ? 'Design Frais' : 'Phase',
  }));
};

// ---------------------------------------------------------------------------
// Build the four financial-summary cards, grouping by currency when needed.
// When all invoices share the same currency the totals are straightforward;
// when they differ we show values in each currency separately.
// ---------------------------------------------------------------------------
const buildFinancialSummary = (invoices) => {
  const today = new Date().toISOString().split('T')[0];

  // Group totals by currency so we never mix currencies in a single number
  const byCurrency = {};
  const ensure = (code) => {
    if (!byCurrency[code]) {
      byCurrency[code] = { total: 0, pending: 0, paid: 0, overdue: 0 };
    }
  };

  invoices.forEach((inv) => {
    const code = inv.currencyCode || 'EUR';
    ensure(code);
    const amount = parseFloat(inv.totalAmount || 0);
    const paid = parseFloat(inv.amountPaid || 0);

    byCurrency[code].total += amount;

    if (['paid'].includes(inv.status)) {
      byCurrency[code].paid += paid;
    } else if (['overdue'].includes(inv.status) || (inv.dueDate && inv.dueDate < today && inv.status !== 'paid')) {
      byCurrency[code].overdue += parseFloat(inv.balanceDue || 0);
    } else if (['draft', 'sent', 'pending', 'partially_paid'].includes(inv.status)) {
      byCurrency[code].pending += parseFloat(inv.balanceDue || 0);
    }
  });

  const currencies = Object.keys(byCurrency);

  const format = (field) =>
    currencies
      .map((c) => formatCurrency(byCurrency[c][field], c))
      .join(' + ') || formatCurrency(0, 'EUR');

  const countByStatus = (statuses) =>
    invoices.filter((i) => statuses.includes(i.status)).length;

  const overdueCount = invoices.filter(
    (i) => i.dueDate && i.dueDate < today && i.status !== 'paid'
  ).length;

  return [
    {
      label: 'Total Revenue',
      value: format('total'),
      rawValue: currencies.reduce((s, c) => s + byCurrency[c].total, 0),
      change: `${invoices.length} invoice${invoices.length !== 1 ? 's' : ''}`,
      trend: 'up',
    },
    {
      label: 'Outstanding',
      value: format('pending'),
      rawValue: currencies.reduce((s, c) => s + byCurrency[c].pending, 0),
      change: `${countByStatus(['draft', 'sent', 'pending', 'partially_paid'])} invoice(s)`,
      trend: 'neutral',
    },
    {
      label: 'Paid This Month',
      value: format('paid'),
      rawValue: currencies.reduce((s, c) => s + byCurrency[c].paid, 0),
      change: `${countByStatus(['paid'])} invoice(s)`,
      trend: 'up',
    },
    {
      label: 'Overdue',
      value: format('overdue'),
      rawValue: currencies.reduce((s, c) => s + byCurrency[c].overdue, 0),
      change: `${overdueCount} invoice(s)`,
      trend: 'down',
    },
  ];
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const BillingOverview = () => {
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [showEditModal, setShowEditModal] = useState(false);
const [invoiceToEdit, setInvoiceToEdit] = useState(null);
const handleEditInvoice = (invoice) => {
  setInvoiceToEdit(invoice);
  setShowEditModal(true);
};
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    period: 'all',
    minAmount: '',
    maxAmount: '',
    startDate: '',
    endDate: '',
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [financialSummary, setFinancialSummary] = useState([]);

  // Advanced tracking
  const [milestoneSummary, setMilestoneSummary] = useState([]);
  const [progressionLogs, setProgressionLogs] = useState([]);
  const [collectionTeamData, setCollectionTeamData] = useState([]);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [selectedInvoiceForTracking, setSelectedInvoiceForTracking] = useState(null);

  const breadcrumbItems = [
    { label: 'Tableau de bord', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'Facturation', path: '/billing-overview', icon: 'CreditCard' },
  ];

  // ── Data fetching ──────────────────────────────────────────────────────────

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchInvoicesFromSupabase();
      setInvoices(data);
      setFilteredInvoices(data);
      setFinancialSummary(buildFinancialSummary(data));
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError('Erreur lors du chargement des factures');
      setInvoices([]);
      setFilteredInvoices([]);
      setFinancialSummary([
        { label: 'Total Revenue', value: formatCurrency(0), change: '0 invoices', trend: 'up' },
        { label: 'Outstanding', value: formatCurrency(0), change: '0 invoices', trend: 'neutral' },
        { label: 'Paid This Month', value: formatCurrency(0), change: '0 invoices', trend: 'up' },
        { label: 'Overdue', value: formatCurrency(0), change: '0 invoices', trend: 'down' },
      ]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Auto-select first invoice for tracking
  useEffect(() => {
    if (invoices?.length > 0 && !selectedInvoiceForTracking) {
      setSelectedInvoiceForTracking(invoices[0]?.id);
    }
  }, [invoices]);

  const fetchTrackingData = useCallback(async (invoiceId) => {
    if (!invoiceId) return;
    try {
      setTrackingLoading(true);
      const [milestones, logs, teamData] = await Promise.all([
        advancedBillingService?.getMilestoneSummaryByInvoice(invoiceId),
        advancedBillingService?.getProgressionLogsByInvoice(invoiceId),
        advancedBillingService?.getCollectionTeamByInvoice(invoiceId),
      ]);
      setMilestoneSummary(milestones || []);
      setProgressionLogs(logs || []);
      setCollectionTeamData(teamData || []);
    } catch (err) {
      console.error('Error fetching tracking data:', err);
    } finally {
      setTrackingLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedInvoiceForTracking) {
      fetchTrackingData(selectedInvoiceForTracking);
    }
  }, [selectedInvoiceForTracking, fetchTrackingData]);

  // ── CRUD handlers ──────────────────────────────────────────────────────────
// ADD this handler alongside handleEditInvoice:
const handleEditInvoiceById = async (invoiceId) => {
  const found = invoices.find((i) => i.id === invoiceId);
  if (found) {
    setInvoiceToEdit(found);
    setShowEditModal(true);
    return;
  }
  // Fallback: fetch from Supabase if not in loaded list
  try {
    const { data, error } = await supabase
      .from('invoices')
      .select('*, countries(currency_code, country_name)')
      .eq('id', invoiceId)
      .single();
    if (error) throw error;
    setInvoiceToEdit({
      ...data,
      invoiceNumber: data.invoice_number,
      clientName: data.client_name,
      clientEmail: data.client_email,
      issueDate: data.issue_date,
      dueDate: data.due_date,
      paymentDate: data.payment_date,
      totalAmount: data.total_amount,
      amountPaid: data.amount_paid,
      balanceDue: data.balance_due,
      paymentMethod: data.payment_method,
      currencyCode: data.countries?.currency_code || 'EUR',
    });
    setShowEditModal(true);
  } catch (err) {
    console.error(err);
    toast.error('Impossible de charger la facture');
  }
};
  const handleUpdateStatus = async (id, status) => {
    try {
      const { error } = await supabase
        .from('invoices')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      await fetchInvoices();
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const handleDeleteInvoice = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette facture ?')) return;
    try {
      const { error } = await supabase.from('invoices').delete().eq('id', id);
      if (error) throw error;
      await fetchInvoices();
    } catch (err) {
      console.error('Error deleting invoice:', err);
      alert('Erreur lors de la suppression de la facture');
    }
  };

  const handlePaymentSubmit = async (invoiceId, paymentData) => {
    try {
      const invoice = invoices.find((i) => i.id === invoiceId);
      const newAmountPaid =
        parseFloat(invoice?.amountPaid || 0) + parseFloat(paymentData?.amount || 0);
      const newBalanceDue = Math.max(
        0,
        parseFloat(invoice?.totalAmount || 0) - newAmountPaid
      );
      const newStatus = newBalanceDue <= 0 ? 'paid' : 'partially_paid';

      const { error } = await supabase
        .from('invoices')
        .update({
          amount_paid: newAmountPaid,
          balance_due: newBalanceDue,
          status: newStatus,
          payment_date: paymentData?.paymentDate || new Date().toISOString().split('T')[0],
          payment_method: paymentData?.paymentMethod || invoice?.paymentMethod,
          updated_at: new Date().toISOString(),
        })
        .eq('id', invoiceId);

      if (error) throw error;
      await fetchInvoices();
      setShowPaymentModal(false);
      alert('Paiement enregistré avec succès!');
    } catch (err) {
      console.error('Error recording payment:', err);
      alert("Erreur lors de l'enregistrement du paiement");
    }
  };

  const handleDownloadInvoice = async (invoice) => {
    alert(`Téléchargement de la facture ${invoice?.invoiceNumber} en cours de développement`);
  };

  const handleSendEmail = async (invoiceId) => {
    alert(`Envoi email pour la facture ${invoiceId} en cours de développement`);
  };

  const handleRemoveCollectionMember = async (assignmentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir retirer ce membre de l\'équipe ?')) return;
    try {
      await advancedBillingService?.removeCollectionTeamMember(assignmentId);
      if (selectedInvoiceForTracking) await fetchTrackingData(selectedInvoiceForTracking);
      alert('Membre retiré avec succès!');
    } catch (err) {
      console.error('Error removing collection team member:', err);
      alert('Erreur lors du retrait du membre');
    }
  };

  // ── Client-side filtering ──────────────────────────────────────────────────

  useEffect(() => {
    let result = [...invoices];
    const today = new Date().toISOString().split('T')[0];

    if (filters.search) {
      const q = filters.search.toLowerCase();
      result = result.filter(
        (inv) =>
          inv.invoiceNumber?.toLowerCase().includes(q) ||
          inv.clientName?.toLowerCase().includes(q)
      );
    }

    if (filters.status !== 'all') {
      result = result.filter((inv) => inv.status === filters.status);
    }

    if (filters.minAmount) {
      result = result.filter(
        (inv) => parseFloat(inv.totalAmount) >= parseFloat(filters.minAmount)
      );
    }

    if (filters.maxAmount) {
      result = result.filter(
        (inv) => parseFloat(inv.totalAmount) <= parseFloat(filters.maxAmount)
      );
    }

    if (filters.period !== 'all' && filters.period !== 'custom') {
      const cutoff = new Date();
      if (filters.period === 'today') cutoff.setHours(0, 0, 0, 0);
      else if (filters.period === 'week') cutoff.setDate(cutoff.getDate() - 7);
      else if (filters.period === 'month') cutoff.setMonth(cutoff.getMonth() - 1);
      else if (filters.period === 'quarter') cutoff.setMonth(cutoff.getMonth() - 3);
      else if (filters.period === 'year') cutoff.setFullYear(cutoff.getFullYear() - 1);
      result = result.filter((inv) => new Date(inv.issueDate) >= cutoff);
    }

    if (filters.period === 'custom' && filters.startDate && filters.endDate) {
      result = result.filter((inv) => {
        const d = new Date(inv.issueDate);
        return d >= new Date(filters.startDate) && d <= new Date(filters.endDate);
      });
    }

    setFilteredInvoices(result);
  }, [filters, invoices]);

  const handleFilterChange = (key, value) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const handleResetFilters = () =>
    setFilters({ search: '', status: 'all', period: 'all', minAmount: '', maxAmount: '', startDate: '', endDate: '' });

  // ── Total displayed in the table footer ──────────────────────────────────

  const tableTotalLabel = (() => {
    const byCurr = {};
    filteredInvoices.forEach((inv) => {
      const code = inv.currencyCode || 'EUR';
      byCurr[code] = (byCurr[code] || 0) + parseFloat(inv.totalAmount || 0);
    });
    return Object.entries(byCurr)
      .map(([code, val]) => formatCurrency(val, code))
      .join(' + ') || formatCurrency(0);
  })();

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
      <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
        <Breadcrumb items={breadcrumbItems} />

        <div className="px-6 py-6 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Aperçu de la facturation</h1>
              <p className="text-muted-foreground mt-2">
                Gérez vos factures et suivez les paiements de votre clinique
              </p>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-card border border-border rounded-lg p-6 animate-pulse">
                  <div className="h-4 bg-muted rounded w-3/4 mb-3" />
                  <div className="h-8 bg-muted rounded w-1/2 mb-2" />
                  <div className="h-3 bg-muted rounded w-full" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-error mb-4">{error}</p>
              <button
                onClick={fetchInvoices}
                className="px-4 py-2 bg-primary text-white rounded-lg"
              >
                Réessayer
              </button>
            </div>
          ) : (
            <>
              {/* Financial Summary — values are pre-formatted strings with correct currency */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {financialSummary.map((summary, index) => (
                  <FinancialSummaryCard key={index} {...summary} />
                ))}
              </div>

              <InvoiceFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onReset={handleResetFilters}
                onExport={() => alert("Fonctionnalité d'export en cours de développement")}
              />

              {/* Invoice selector for detailed tracking */}
              {invoices?.length > 0 && (
                <div className="bg-card rounded-lg p-4 border border-border shadow-card">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Sélectionner une facture pour le suivi détaillé :
                  </label>
                  <select
                    value={selectedInvoiceForTracking || ''}
                    onChange={(e) => setSelectedInvoiceForTracking(e.target.value)}
                    className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  >
                    {invoices.map((invoice) => (
                      <option key={invoice.id} value={invoice.id}>
                        {invoice.invoiceNumber} — {invoice.clientName} —{' '}
                        {formatCurrency(invoice.totalAmount, invoice.currencyCode)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Milestone Status */}
              {selectedInvoiceForTracking && (
                <div>
                  <h2 className="text-xl font-semibold text-foreground mb-4">
                    Statut des échéanciers pour la facture {invoices.find(i => i.id === selectedInvoiceForTracking)?.invoiceNumber}  
                  </h2>
                  {trackingLoading ? (
                    <div className="text-center py-8 text-muted-foreground">
                      Chargement des données de suivi…
                    </div>
                  ) : milestoneSummary?.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {milestoneSummary.map((phaseSummary) => (
                       <MilestoneStatusCard
    key={phaseSummary?.phaseId}
    phaseSummary={phaseSummary}
    onUpdate={() => fetchTrackingData(selectedInvoiceForTracking)}
    onEditInvoice={handleEditInvoiceById}   // ← ADD THIS
  />
                      ))}
                    
       <div className="pt-3 border-t border-gray-100 col-span-full">
    <button
      onClick={() => handleEditInvoiceById(selectedInvoiceForTracking)}
      className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-semibold transition-colors"
    >
      <FileEdit className="w-3.5 h-3.5" />
      Modifier la facture
    </button>
  </div>
                    </div>
                    
                  ) : (
                    <div className="bg-card rounded-lg border border-border p-6 text-center text-muted-foreground">
                      Aucun échéancier configuré pour cette facture
                    </div>
                  )}
                </div>
              )}

         

            {/* {selectedInvoiceForTracking && (
                <CollectionTeamSection
                  collectionTeamData={collectionTeamData}
                  onAssignMember={(phaseId) =>
                    alert(`Assignation pour la phase ${phaseId} en cours de développement`)
                  }
                  onRemoveMember={handleRemoveCollectionMember}
                />
              )} */} 

              {/* Invoice Table */}
              <div style={{ marginTop: '150px' }} className="bg-card rounded-lg p-6 border border-border shadow-card mt-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold text-foreground">
                  Tous les Factures ({filteredInvoices?.length})
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Total : {tableTotalLabel}
                  </p>
                </div>

            <InvoiceTable
  invoices={filteredInvoices}
  onViewDetails={(invoice) => {
    setSelectedInvoice(invoice);
    setShowDetailsModal(true);
    setSelectedInvoiceForTracking(invoice?.id);
  }}
  onUploadPayment={(invoice) => {
    setSelectedInvoice(invoice);
    setShowPaymentModal(true);
  }}
  onUpdateStatus={handleUpdateStatus}
  onDelete={handleDeleteInvoice}
  onEdit={handleEditInvoice}  
  formatCurrency={formatCurrency}
/>
              </div>
            </>
          )}
        </div>
      </main>

      {showPaymentModal && selectedInvoice && (
        <PaymentUploadModal
          invoice={selectedInvoice}
          onClose={() => setShowPaymentModal(false)}
          onSubmit={handlePaymentSubmit}
        />
      )}

      {showDetailsModal && selectedInvoice && (
        <InvoiceDetailsModal
          invoice={selectedInvoice}
          onClose={() => setShowDetailsModal(false)}
          onDownload={handleDownloadInvoice}
          formatCurrency={formatCurrency}
        />
      )}

      {showEditModal && invoiceToEdit && (
  <EditInvoiceModal
    invoice={invoiceToEdit}
    onClose={() => {
      setShowEditModal(false);
      setInvoiceToEdit(null);
    }}
    onSaved={fetchInvoices}
  />
)}
    </div>

  );
};

export default BillingOverview;